import { action, computed, makeObservable, reaction } from "mobx";
import type { Api } from "../core/apis";
import { BaseModel, type BaseModelData } from "../core/BaseModel";
import type { Stores } from "../core/stores";
import { trackEvent } from "../core/trackEvent";
import { PlanModelStep } from "../plans/PlanModel";
import { log } from "../ui/log";
import type UserModel from "./UserModel";

type PlanData = { title: string; planUid: string };
type BusinessPlanData = { title: string; businessPlanUid: string };

export interface SuperUserModelData extends BaseModelData {
  businessPlans: BusinessPlanData[];
  plans: PlanData[];
  archivedPlans: PlanData[];
  email: string | null;
  avatarUrl: string | null;
  displayName: string;
  expoPushToken: string | null;
  isDev: boolean;
}

export default class SuperUserModel extends BaseModel<SuperUserModelData> {
  constructor(stores: Stores, api: Api, data: SuperUserModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      businessPlans: data.businessPlans ?? [],
      plans: data.plans ?? [],
      archivedPlans: data.archivedPlans ?? [],
      expoPushToken: data.expoPushToken ?? null,
      avatarUrl: data.avatarUrl ?? null,
      email: data.email ?? null,
      displayName: data.displayName ?? "",
      isDev: data.isDev ?? false,
    });

    makeObservable(this);

    // Make sure the superUser data are updated if you enter a new Planira or update Avatar
    reaction(
      () => this.users,
      async (users) => {
        let gotChange = false;
        let gotChangeReason = "";
        users.forEach((u) => {
          const planFound = this.data.plans.find(
            (p) => u.data.planUid === p.planUid,
          );

          if (!planFound) {
            if (u.plan && !u.businessPlan) {
              this.data.plans.push({
                planUid: u.plan.uid,
                title: u.plan.title ?? "",
              });

              // Make sure to keep archived in sync
              this.data.archivedPlans = this.data.archivedPlans.filter(
                (p) => p.planUid !== u.plan?.uid,
              );

              gotChange = true;
              gotChangeReason = "plan_not_found";

              if (this.data.plans.length > 1) {
                u.plan.trackProgress(
                  PlanModelStep.hasUsersWithMultiplePlans,
                  true,
                );
              }
            }
          } else if (u.plan?.title && u.plan.title !== planFound.title) {
            planFound.title = u.plan.title;
            gotChange = true;
            gotChangeReason = "plan_title_not_synced";
          }

          if (u.data.businessPlanUid) {
            const businessPlanFound = this.data.businessPlans.find(
              (p) => u.data.businessPlanUid === p.businessPlanUid,
            );
            if (!businessPlanFound && u.businessPlan) {
              this.data.businessPlans.push({
                title: u.businessPlan.title,
                businessPlanUid: u.businessPlan.uid,
              });
              gotChange = true;
              gotChangeReason = "business_not_found";
            } else if (
              businessPlanFound &&
              u.businessPlan &&
              businessPlanFound.title !== u.businessPlan.title &&
              !!u.businessPlan.title
            ) {
              businessPlanFound.title = u.businessPlan.title;
              gotChange = true;
              gotChangeReason = "business_title_not_synced";
            }
          }

          if (u.data.avatarUrl && !this.data.avatarUrl) {
            this.data.avatarUrl = u.data.avatarUrl;
            gotChange = true;
            gotChangeReason = "plan_avatar_not_synced";
          }

          if (!!u.data.displayName && !this.data.displayName) {
            this.data.displayName = u.data.displayName;
            gotChange = true;
            gotChangeReason = "plan_displayName_not_synced";
          }
        });

        if (gotChange) {
          console.info("got change", { gotChange, gotChangeReason });

          await this.api.superUsersApi.set(this);
        }
      },
      { fireImmediately: true },
    );
  }

  @action async setDisplayName(name: string) {
    if (name && name !== this.data.displayName) {
      this.data.displayName = name;
      this.api.superUsersApi.update(this.uid, {
        displayName: this.data.displayName,
      });
    }
  }

  @action async setEmail(email: string) {
    if (email && email !== this.data.email) {
      this.data.email = email;
      this.api.superUsersApi.update(this.uid, {
        email: this.data.email,
      });
    }
  }

  @action updatePushToken(newToken: string) {
    if (this.data.expoPushToken !== newToken) {
      this.data.expoPushToken = newToken;
      this.api.superUsersApi.set(this);
      trackEvent("super_user_push_token_set");
      log.dev("Push token saved");
    } else {
      log.dev("Push - No need to update token");
    }
  }

  @action verifySuperUserEmail() {
    const email = this.stores.sessionStore.authUser?.email;

    if (!this.data.email && !!email) {
      this.data.email = email;
      this.api.superUsersApi.set(this);
      trackEvent("super_user_email_set");
    }
  }

  @action archivePlanUid(planUid: string) {
    const planFound = this.data.plans.find((p) => p.planUid === planUid);
    if (planFound) {
      this.data.plans = this.data.plans.filter((p) => p.planUid !== planUid);
      this.data.archivedPlans.push(planFound);

      // Delete all local saved user for that plan first
      this.stores.usersStore.all.forEach((user) => {
        if (user.data.planUid === planUid) {
          this.stores.usersStore.deleteItem(user.uid);
        }
      });

      return this.api.superUsersApi.set(this);
    }
  }

  @action unArchivePlanUid(planUid: string) {
    const planFound = this.data.archivedPlans.find(
      (p) => p.planUid === planUid,
    );
    if (planFound) {
      this.data.archivedPlans = this.data.archivedPlans.filter(
        (p) => p.planUid !== planUid,
      );
      this.data.plans.push(planFound);

      return this.api.superUsersApi.set(this);
    }
  }

  @action setAvatarUrl(url: string | null) {
    this.data.avatarUrl = url;
    return this.api.superUsersApi.set(this);
  }

  @computed get users(): UserModel[] {
    return this.stores.usersStore.all.filter((u) =>
      u.data.authedUsers.includes(this.data.uid),
    );
  }

  toFireStore(): Omit<SuperUserModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
